import React, { FC, useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { Loader } from 'common';
import { PathNames } from './constants';
import { getCurrentRoutes, publicRouts } from './routes';
import { LeavePageModal } from 'features';
import { ProtectedRoute } from './ProtectedRoute';
import CommonErrorModal from 'common/components/CommonErrorModal';
import { useAppDispatch, useAppSelector } from 'common/hooks/redux';
import { RoutesProps } from 'common/types/routes';
import { loginAllTabs, logout, logoutAllTabs } from 'common/auth/authService';
import { useChatsClient } from 'features/feature-chats/hooks/useChatsClient';
import { usePushNotifications } from 'features/feature-chats/hooks/usePushNotifications';
import {
  getProfilesFromSessionStorage,
  getUserRole,
} from 'applicaiton/sessionStorage/auth';
import { useUserTimeZone } from 'common/hooks/useUserTimeZone';
import timezone from 'dayjs/plugin/timezone';
import { useCheckUserRole } from 'common/hooks/useCheckUserRole';
import { UserRoles } from '../constants/userRoles';
import { getSubscriptions } from 'applicaiton/store/reducers/ClinicOwner/ActionCreators';
import NoInternetPage from 'common/components/NoInternetPage';
dayjs.extend(timezone);

export const AppRouter: FC = () => {
  const [online, setOnline] = useState(true);
  const { isErrorModalVisible } = useAppSelector((state) => state.error);
  const { tz } = useUserTimeZone();
  const { isAuth, profile } = useAppSelector((state) => state.users);
  const { initChatClient, shutdownClient, currentClient } = useChatsClient();
  const { initPushNotifications } = usePushNotifications();
  const [routes, setRoutes] = useState<RoutesProps[]>(getCurrentRoutes());
  const { currentClinicOwner } = useAppSelector(
    (state) => state.clinicOwnerSlice,
  );
  const profiles = getProfilesFromSessionStorage();
  const { correctUserRole } = useCheckUserRole();
  const dispatch = useAppDispatch();
  const userRole = getUserRole();
  const isClinicOwner = userRole?.includes(UserRoles.clinicOwner);
  const locationPathName = window.location.pathname;

  dayjs.tz.setDefault(tz);

  useEffect(() => {
    window.addEventListener('online', () => setOnline(true));
    window.addEventListener('offline', () => setOnline(false));

    return () => {
      window.removeEventListener('online', () => setOnline(true));
      window.removeEventListener('offline', () => setOnline(false));
    };
  });

  useEffect(() => {
    if (isAuth && isClinicOwner && online) {
      dispatch(getSubscriptions());
    }
  }, [isAuth, locationPathName, online]);

  useEffect(() => {
    if (!correctUserRole) {
      logout();
    } else {
      const routesList = getCurrentRoutes();
      setRoutes(routesList);
    }
  }, [
    isAuth,
    profile,
    currentClinicOwner?.professional,
    currentClinicOwner?.professional?.id,
    correctUserRole,
    online,
  ]);

  useEffect(() => {
    logoutAllTabs();
    loginAllTabs();
  }, []);
  // Client initialization force to router to have the information about the availavility status and push notifications.
  useEffect(() => {
    if (!isAuth && !profiles) {
      return;
    }
    initChatClient();
    return () => {
      shutdownClient();
    };
  }, [currentClinicOwner?.professional]);

  useEffect(() => {
    if (currentClient) {
      initPushNotifications(currentClient);
    }
  }, [currentClient]);

  return (
    <BrowserRouter>
      {online && (
        <Routes>
          {routes.map(({ path, component }) => (
            <Route
              path={path}
              element={<ProtectedRoute>{component}</ProtectedRoute>}
              key={path}
            />
          ))}
          {publicRouts.map(({ path, component }) => (
            <Route path={path} element={component} key={path} />
          ))}
          <Route path="/*" element={<Navigate to={PathNames.login} />} />
        </Routes>
      )}

      <Loader />
      <LeavePageModal />
      {isErrorModalVisible && <CommonErrorModal isOpen={isErrorModalVisible} />}
      <NoInternetPage online={online} />
    </BrowserRouter>
  );
};
